// Phase 2.10 (hh-1hz) — PlacePicker.
//
// Ported from designproject/files/screens-forms.jsx (`PlacePicker`). The
// design grouped room chips under their property heading and selected a
// single room id. Here the property tree is supplied by the caller (it owns
// the usePropertyTree hook so loading / error state is rendered once around
// the whole form). Selection yields both ids so the API can be called with a
// concrete propertyId / roomId.

import type { JSX } from 'preact';
import { T, Icon, entityIcon } from '../../tokens';
import type { PropertyTreeNode } from '../../api/endpoints/properties.js';

export interface PlaceSelection {
  readonly propertyId: string;
  readonly roomId: string;
}

export interface PlacePickerProps {
  readonly tree: readonly PropertyTreeNode[];
  readonly value: PlaceSelection | null;
  readonly onChange: (sel: PlaceSelection) => void;
}

export function PlacePicker({ tree, value, onChange }: PlacePickerProps): JSX.Element {
  const withRooms = tree.filter((p) => p.rooms.length > 0);

  if (withRooms.length === 0) {
    return (
      <div
        style={{
          padding: 14,
          background: T.surfaceAlt,
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          fontSize: 12.5,
          color: T.ink3,
        }}
      >
        Нет помещений — сначала добавь хотя бы одно.
      </div>
    );
  }

  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      {withRooms.map((p, pi) => (
        <div key={p.id}>
          {pi > 0 && <div style={{ height: 1, background: T.divider }} />}
          {/* hh-l4a: иконка типа собственности рядом с её названием. */}
          <div
            style={{
              padding: '10px 14px 6px',
              fontSize: 11.5,
              fontWeight: 700,
              color: T.ink3,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name={entityIcon('property', { name: p.name })} size={13} color={T.ink3} />
            {p.name}
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              padding: '0 12px 12px',
            }}
          >
            {p.rooms.map((r) => {
              const selected = value !== null && value.roomId === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onChange({ propertyId: p.id, roomId: r.id })}
                  style={{
                    padding: '8px 12px',
                    background: selected ? T.brand : T.bg,
                    color: selected ? '#fff' : T.ink,
                    border: `1px solid ${selected ? T.brand : T.border}`,
                    borderRadius: 999,
                    fontFamily: T.font,
                    fontSize: 13,
                    fontWeight: 650,
                    cursor: 'pointer',
                  }}
                >
                  {r.name}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// PropertyPicker — flat property chip row (used by AddRoomScreen).
// ─────────────────────────────────────────────────────────

export interface PropertyPickerProps {
  readonly tree: readonly PropertyTreeNode[];
  readonly value: string | null;
  readonly onChange: (propertyId: string) => void;
}

export function PropertyPicker({ tree, value, onChange }: PropertyPickerProps): JSX.Element {
  if (tree.length === 0) {
    return (
      <div
        style={{
          padding: 14,
          background: T.surfaceAlt,
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          fontSize: 12.5,
          color: T.ink3,
        }}
      >
        Нет собственности.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {tree.map((p) => {
        const selected = value === p.id;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.id)}
            style={{
              padding: '8px 12px',
              background: selected ? T.brand : T.surface,
              color: selected ? '#fff' : T.ink,
              border: `1px solid ${selected ? T.brand : T.border}`,
              borderRadius: 999,
              fontFamily: T.font,
              fontSize: 13,
              fontWeight: 650,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon
              name={entityIcon('property', { name: p.name })}
              size={14}
              color={selected ? '#fff' : T.ink2}
            />
            {p.name}
          </button>
        );
      })}
    </div>
  );
}
